/**
 * Apple-idiom control primitives: the switch, slider, stepper, segmented
 * control, icon tile and grouped list row.
 */
import { useId, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, CloseIcon, MinusIcon, PlusIcon, SearchIcon } from './icons.js';

/* ── Switch ───────────────────────────────────────────────────────────── */

export function Switch({ checked, onChange, disabled, label, small }: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  label: string;
  small?: boolean;
}): JSX.Element {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={small ? 'switch switch--sm' : 'switch'}
      onClick={() => onChange(!checked)}
    >
      <span className="switch__knob" />
    </button>
  );
}

/* ── Slider ───────────────────────────────────────────────────────────── */

export function Slider({ value, min, max, step = 1, onChange, label, tint }: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (next: number) => void;
  label: string;
  tint?: string;
}): JSX.Element {
  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;
  return (
    <span className="slider">
      <span className="slider__track">
        <span className="slider__fill" style={{ width: `${pct}%`, background: tint }} />
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-label={label}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </span>
  );
}

/* ── Stepper ──────────────────────────────────────────────────────────── */

export function Stepper({ value, min, max, step = 1, onChange, label }: {
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (next: number) => void;
  label: string;
}): JSX.Element {
  // Steps land on clean multiples rather than drifting off the starting value.
  const clamp = (n: number) => Math.min(max, Math.max(min, Math.round(n / step) * step));
  const decimals = String(step).split('.')[1]?.length ?? 0;
  return (
    <span className="stepper" role="group" aria-label={label}>
      <button type="button" aria-label={`Decrease ${label}`} disabled={value <= min}
        onClick={() => onChange(Number(clamp(value - step).toFixed(decimals)))}>
        <MinusIcon />
      </button>
      <span className="stepper__div" />
      <button type="button" aria-label={`Increase ${label}`} disabled={value >= max}
        onClick={() => onChange(Number(clamp(value + step).toFixed(decimals)))}>
        <PlusIcon />
      </button>
    </span>
  );
}

/* ── Segmented control ────────────────────────────────────────────────── */

export function Segmented<T extends string>({ value, options, onChange, label, grow }: {
  value: T;
  options: { value: T; label: ReactNode }[];
  onChange: (next: T) => void;
  label?: string;
  grow?: boolean;
}): JSX.Element {
  return (
    <div className={grow ? 'segmented segmented--grow' : 'segmented'} role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Search field ─────────────────────────────────────────────────────── */

export function SearchField({ value, onChange, placeholder, label, autoFocus }: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  label: string;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <div className="search">
      <SearchIcon className="search__glyph" />
      <input
        type="search"
        className="search__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        autoFocus={autoFocus}
        autoComplete="off"
        spellCheck={false}
        // Escape clears, which is what the field's own clear button does.
        onKeyDown={(e) => { if (e.key === 'Escape' && value) { e.preventDefault(); onChange(''); } }}
      />
      {value && (
        <button type="button" className="search__clear" aria-label="Clear search" onClick={() => onChange('')}>
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

/**
 * Case- and accent-insensitive substring match across a row's searchable text.
 * Every whitespace-separated term must appear somewhere, so "chan pri" finds
 * Ellie Chan with Priya Raman.
 */
export function matchesSearch(query: string, ...fields: (string | number | null | undefined)[]): boolean {
  const terms = normalise(query).split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const haystack = normalise(fields.filter((f) => f != null && f !== '').join(' '));
  return terms.every((t) => haystack.includes(t));
}

function normalise(value: string | number): string {
  return String(value).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/* ── Icon tile ────────────────────────────────────────────────────────── */

export type TileColor =
  | 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'teal' | 'indigo' | 'pink' | 'gray' | 'yellow';

export function Tile({ color, children, size }: {
  color: TileColor;
  children: ReactNode;
  size?: 'sm' | 'lg';
}): JSX.Element {
  const cls = ['tile', `tile-${color}`, size === 'lg' ? 'tile--lg' : size === 'sm' ? 'tile--sm' : '']
    .filter(Boolean).join(' ');
  return <span className={cls}>{children}</span>;
}

/* ── Grouped list ─────────────────────────────────────────────────────── */

export function List({ header, children, style }: {
  header?: ReactNode;
  children: ReactNode;
  style?: React.CSSProperties;
}): JSX.Element {
  return (
    <div style={style}>
      {header && <div className="list__header">{header}</div>}
      <div className="list">{children}</div>
    </div>
  );
}

/**
 * One list row. Renders as a button when `onClick` is given, a link when `href`
 * is, and a plain div otherwise — so a static row is not announced as pressable.
 */
export function Row({ tile, title, sub, value, onClick, href, chevron, trailing, danger }: {
  tile?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  value?: ReactNode;
  onClick?: () => void;
  href?: string;
  chevron?: boolean;
  trailing?: ReactNode;
  danger?: boolean;
}): JSX.Element {
  const inner = (
    <>
      {tile}
      <span className="list__label">
        <span className="list__title" style={danger ? { color: 'var(--sys-red)' } : undefined}>{title}</span>
        {sub && <span className="list__sub">{sub}</span>}
      </span>
      {value && <span className="list__value">{value}</span>}
      {trailing}
      {chevron && <span className="list__chev"><ChevronRight /></span>}
    </>
  );
  const cls = `list__row${tile ? ' list__row--tiled' : ''}`;
  if (href) return <a className={cls} href={href} target="_blank" rel="noreferrer">{inner}</a>;
  if (onClick) return <button type="button" className={cls} onClick={onClick}>{inner}</button>;
  return <div className={cls}>{inner}</div>;
}

/* ── Menu ─────────────────────────────────────────────────────────────── */

/** A Liquid Glass popover menu, dismissed by clicking away or pressing Escape. */
export function Menu({ trigger, children, align = 'right' }: {
  trigger: (props: { onClick: () => void; 'aria-expanded': boolean; id: string }) => ReactNode;
  children: ReactNode;
  align?: 'left' | 'right';
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrap = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={wrap}
      style={{ position: 'relative' }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}
      onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
    >
      {trigger({ onClick: () => setOpen((v) => !v), 'aria-expanded': open, id })}
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div
            className="menu"
            role="menu"
            aria-labelledby={id}
            style={{ position: 'absolute', top: 'calc(100% + 8px)', [align]: 0, zIndex: 41 }}
            onClick={() => setOpen(false)}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
}

export function MenuItem({ icon, children, onClick, danger, href }: {
  icon?: ReactNode;
  children: ReactNode;
  onClick?: () => void;
  danger?: boolean;
  href?: string;
}): JSX.Element {
  const cls = `menu__item${danger ? ' menu__item--danger' : ''}`;
  if (href) return <a className={cls} role="menuitem" href={href} target="_blank" rel="noreferrer">{icon}<span>{children}</span></a>;
  return <button type="button" className={cls} role="menuitem" onClick={onClick}>{icon}<span>{children}</span></button>;
}

export const MenuSeparator = (): JSX.Element => <div className="menu__sep" role="separator" />;
